import {
    determineTableType,
    transferSettingsOfObject,
    addFlex,
    addFragments,
    convertActionKey,
    importHeaderActions
} from '../common/index';
import type {
    ImportListReportV2Parameters,
    ChangeDefinition,
    CommonImportParameters
} from '@sap/ux-specification-types';
import { LogSeverity, DefinitionName, ManifestSection, PageTypeV2, v2 } from '@sap/ux-specification-types';
import { parseChangeString } from '../../../common';
import { log } from '../../../../extensionLogger';
import i18next from 'i18next';
import { initI18n } from '../../../../i18n/i18n';
import {
    checkV2Page,
    filterFlexChanges,
    findListReportPageV2,
    getV2Pages,
    removeEmptySettings
} from '../../import/utils';
import { MetadataInstanceFactoryV2 as Factory } from '../../export/factory';
import type { MetadataInstanceInterface } from '../../../common/decoration/factory';
import { INLINEDELETETABLE, MULTISELECTTABLE } from '../../types';
import type { Definition } from 'typescript-json-schema';
import type { FlexForTransfer } from '../controls/table';
import { addFlexChangeToConfigTable, importFlexChangesOfColumn } from '../controls/table';

/**
 * Imports filterbar information into the LR config.
 *
 * @param listReportConfig - LR config, to be filled
 * @param factory - factory, for generating the access to reflect-metadata
 * @param importParameters  - object comprising all input data
 * @param schemaDefinitions - app schema definitions
 * @param pageKey - key of the page in manifest
 */
function importFilterBar(
    listReportConfig: v2.ListReportConfigV2,
    factory: MetadataInstanceInterface,
    importParameters: ImportListReportV2Parameters,
    schemaDefinitions: Definition,
    pageKey: string
) {
    listReportConfig.filterBar = factory.createInstance(
        PageTypeV2.ListReport,
        DefinitionName.ListReportFilterBar,
        listReportConfig.filterBar
    );
    transferSettingsOfObject(
        listReportConfig.filterBar,
        importParameters.manifest,
        schemaDefinitions[DefinitionName.ListReportFilterBar],
        pageKey,
        importParameters.logger
    );
    if (schemaDefinitions['SelectionFields']) {
        listReportConfig.filterBar.selectionFields = factory.createInstance(
            PageTypeV2.ListReport,
            DefinitionName.ListReportFilterBar,
            listReportConfig.filterBar.selectionFields
        );
        const schemaSelectionFields = schemaDefinitions['SelectionFields'].properties;
        for (const selectionProperty in schemaSelectionFields) {
            const selectionKey = selectionProperty;
            listReportConfig.filterBar.selectionFields[selectionKey] = factory.createInstance(
                PageTypeV2.ListReport,
                'SelectionFieldDateSettings'
            );
            transferSettingsOfObject(
                listReportConfig.filterBar.selectionFields[selectionKey],
                importParameters.manifest,
                schemaDefinitions['SelectionFieldDateSettings'],
                pageKey,
                importParameters.logger,
                selectionKey
            );
        }
    }
}

/**
 * Imports flex changes for columns and table.
 *
 * @param importParameters  - object comprising all input data
 * @param lineItemsDefinition - the relevant line items' definition in the schema
 * @param listReportConfig - LR config, to be filled
 */
export function importFlexChangesOfTableAndColumns(
    importParameters: CommonImportParameters,
    lineItemsDefinition: Definition,
    listReportConfig: v2.ListReportConfigV2
) {
    if (!importParameters.flex) {
        return;
    }

    //Flex changes for table
    importParameters.flex.forEach((changeString) => {
        const change = parseChangeString(changeString) as ChangeDefinition;

        //Prepare the flex change for transfer to the flexibility utility function
        const flexForTransfer: FlexForTransfer = {
            controlId: change.controlId,
            propertyId: change.content?.property,
            newValue: change.content?.newBinding ? change.content.newBinding : change.content?.newValue,
            controlType: change.controlType,
            creation: change.creation
        };
        //Import the currently processed flex change if it applies to a property of the SmartTable or its inner table
        addFlexChangeToConfigTable(listReportConfig.table, flexForTransfer, lineItemsDefinition);
    });

    //Flex changes for columns
    const columnsDefinition = (lineItemsDefinition.properties.columns as Definition).$ref.split('/').pop();
    const columns =
        ((importParameters.jsonSchema as Definition).definitions[columnsDefinition] as Definition)?.properties || {};
    for (const column in columns) {
        //custom is just a placeholder for the custom columns, we do not need to consider flex changes for it
        if (column === 'custom') {
            continue;
        }
        //Import the flex changes for the given column
        importFlexChangesOfColumn(importParameters, listReportConfig, column);
    }
}

/**
 * Imports table information into the LR config.
 *
 * @param tableType - table type
 * @param schemaDefinitions - app schema definitions
 * @param listReportConfig - LR config, to be filled
 * @param importParameters  - object comprising all input data
 * @param pageKey - key of the page in manifest
 */
function importTable(
    tableType: v2.TableTypeV2,
    schemaDefinitions: Definition,
    listReportConfig: v2.ListReportConfigV2,
    importParameters: ImportListReportV2Parameters,
    pageKey: string
) {
    let lineItemsDefinition: Definition;

    if (tableType === 'TreeTable' || tableType === 'AnalyticalTable' || tableType === 'GridTable') {
        lineItemsDefinition = schemaDefinitions[tableType + '<LineItems>'];
    } else {
        if (schemaDefinitions[MULTISELECTTABLE] && schemaDefinitions[INLINEDELETETABLE]) {
            lineItemsDefinition = JSON.parse(JSON.stringify(schemaDefinitions[MULTISELECTTABLE])) as Definition;
            lineItemsDefinition.properties = {
                ...lineItemsDefinition.properties,
                ...(schemaDefinitions[INLINEDELETETABLE] as Definition).properties
            };
        }
    }
    // Import table settings
    transferSettingsOfObject(
        listReportConfig.table,
        importParameters.manifest,
        lineItemsDefinition,
        pageKey,
        importParameters.logger
    );
    // Import flex changes for columns
    importFlexChangesOfTableAndColumns(importParameters, lineItemsDefinition, listReportConfig);

    if (
        (!schemaDefinitions['LineItems']?.properties ||
            Object.keys(schemaDefinitions['LineItems'].properties).filter((item) => item !== 'custom').length < 1) &&
        !listReportConfig.table.quickVariantSelection &&
        !listReportConfig.table.quickVariantSelectionX
    ) {
        log(importParameters.logger, {
            severity: LogSeverity.Error,
            message: i18next.t('NOLINEITEMS')
        });
    }
}

/**
 * Adds manifest settings to the config.json of List Report V2.
 *
 * @param importParameters  - object comprising all input data
 * @param listReportConfig - the configuration to be filled
 * @param factory - factory, for generating the access to reflect-metadata
 * @param pageKey - key of the page in manifest
 */
function addSettings(
    importParameters: ImportListReportV2Parameters,
    listReportConfig: v2.ListReportConfigV2,
    factory: MetadataInstanceInterface,
    pageKey: string
): void {
    const schemaDefinitions = (importParameters.jsonSchema as Definition).definitions as Definition;
    const tableType = determineTableType(importParameters.manifest, pageKey);
    listReportConfig.table = factory.createInstance(PageTypeV2.ListReport, tableType, listReportConfig.table);

    // Import standard header actions:
    importHeaderActions(listReportConfig, factory, schemaDefinitions);

    //Import table:
    importTable(tableType, schemaDefinitions, listReportConfig, importParameters, pageKey);

    //Import ToolBar:
    listReportConfig.table.toolBar = factory.createInstance(PageTypeV2.ListReport, DefinitionName.ToolBar, {
        actions: {}
    });
    transferSettingsOfObject(
        listReportConfig.table.toolBar,
        importParameters.manifest,
        schemaDefinitions[DefinitionName.ToolBarLR],
        pageKey,
        importParameters.logger
    );
    if (schemaDefinitions[DefinitionName.ActionsLR]) {
        for (const actionProperty in schemaDefinitions[DefinitionName.ActionsLR].properties) {
            const actionKey = convertActionKey(actionProperty);
            listReportConfig.table.toolBar.actions[actionKey] = factory.createInstance(
                PageTypeV2.ListReport,
                DefinitionName.Action
            );
            transferSettingsOfObject(
                listReportConfig.table.toolBar.actions[actionKey],
                importParameters.manifest,
                schemaDefinitions[DefinitionName.Action],
                pageKey,
                importParameters.logger
            );
        }
    }

    //Import FilterBar:
    importFilterBar(listReportConfig, factory, importParameters, schemaDefinitions, pageKey);

    //Import Footer:
    listReportConfig.footer = { actions: {} };
    const footerAction = schemaDefinitions[DefinitionName.FooterActionsLR];
    if (footerAction) {
        for (const actionProperty in footerAction.properties) {
            const actionKey = convertActionKey(actionProperty);
            listReportConfig.footer.actions[actionKey] = factory.createInstance(
                PageTypeV2.ListReport,
                DefinitionName.FooterAction
            );
            transferSettingsOfObject(
                listReportConfig.footer.actions[actionKey],
                importParameters.manifest,
                schemaDefinitions[DefinitionName.Action],
                pageKey,
                importParameters.logger
            );
        }
    }
}

/**
 * Creates the configuration file content for a list report V2.
 *
 * @param importParameters  - object comprising all input data
 * @returns {v2.ListReportConfigV2 | undefined} - the configuration (JSON) for the list report
 */
export function createListReportConfig(
    importParameters: ImportListReportV2Parameters
): v2.ListReportConfigV2 | undefined {
    // Initialize i18next
    initI18n();
    // Filter flex changes which are relevant only for page
    importParameters = {
        ...importParameters,
        flex: filterFlexChanges(importParameters.flex, v2.SAPUI5_VIEW_EXTENSION_LIST_REPORT, importParameters.entitySet)
    };

    const v2Pages = getV2Pages(importParameters);
    if (!v2Pages) {
        return;
    }
    const factory = new Factory();
    //Instantiate Page
    const config = factory.createPageInstance(PageTypeV2.ListReport);

    const pageKeys: string[] = [];
    const v2Page = findListReportPageV2(
        importParameters.manifest[ManifestSection.generic].pages as v2.SapUiAppPagesV2,
        pageKeys
    );
    checkV2Page(v2Page, importParameters.logger, v2Page?.entitySet);
    if (!v2Page) {
        return;
    }

    //Add manifest settings to config
    addSettings(importParameters, config, factory, pageKeys[0]);

    // Add flex changes to config
    importParameters.flex.forEach((change) => {
        if (parseChangeString(change)) {
            addFlex(config, change, importParameters.jsonSchema, importParameters.logger);
        }
    });
    addFragments(config, importParameters, v2Page, PageTypeV2.ListReport);
    removeEmptySettings(config);
    return config;
}
